import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
  useTheme,
} from '@mui/material';
import { useIntl } from 'react-intl';

import { Button } from '@verantos-dev/shared-components';

import { useTestBatchConfirmDialog } from './useTestBatchConfirmDialog';

interface TestBatchConfirmDialogProps {
  open: boolean;
  encounterCount: number;
  onCancel: () => void;
  onConfirm: () => void;
}

export const TestBatchConfirmDialog = ({
  open,
  encounterCount,
  onCancel,
  onConfirm,
}: TestBatchConfirmDialogProps) => {
  const theme = useTheme();
  const { formatMessage } = useIntl();
  const { acknowledged, setAcknowledged, handleClose, handleConfirm } = useTestBatchConfirmDialog({
    onCancel,
    onConfirm,
  });

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
        <WarningAmberOutlinedIcon sx={{ color: theme.palette.warning.main }} />
        {formatMessage({ id: 'adminProjects.generateAccuracy.testBatchDialog.title' })}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" sx={{ mb: theme.spacing(4) }}>
          {formatMessage(
            { id: 'adminProjects.generateAccuracy.testBatchDialog.body' },
            { count: encounterCount }
          )}
        </Typography>
        <Box
          sx={{
            p: theme.spacing(2),
            borderRadius: 1,
            backgroundColor: theme.palette.grey[100],
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={formatMessage({
              id: 'adminProjects.generateAccuracy.testBatchDialog.acknowledge',
            })}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: theme.spacing(4), pb: theme.spacing(4) }}>
        <Button
          id="test-batch-dialog-cancel"
          variant="text"
          displayLabel={formatMessage({
            id: 'adminProjects.generateAccuracy.testBatchDialog.cancel',
          })}
          sx={{
            textTransform: 'none',
          }}
          onClickHandler={handleClose}
        />
        <Button
          id="test-batch-dialog-confirm"
          variant="contained"
          startIcon={<ScienceOutlinedIcon />}
          disabled={!acknowledged}
          displayLabel={formatMessage({
            id: 'adminProjects.generateAccuracy.testBatchDialog.confirm',
          })}
          onClickHandler={handleConfirm}
          sx={{
            backgroundColor: acknowledged ? theme.palette.warning.main : theme.palette.grey[400],
            fontWeight: 700,
          }}
        />
      </DialogActions>
    </Dialog>
  );
};
